import { Box, Autocomplete, TextField, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Loader } from "../../components/loader/Loader";
import { openPopup } from "../../stateManagement/actions/popupActions";
import "./PjpApproval.css";
import {
  TranslationEnum,
  usePortalTranslation,
} from "../../providers/TranslationProvider";
import DefaultHeading from "../../components/heading/DefaultHeading";
import { User } from "./PjpApprovalTypes";
import {
  getUser,
  getPjpData,
  transformPjpSummary,
  transformDayCard,
  PjpPlanConfig,
  submitPjpApprovalData,
} from "./PjpApprovalServices";
import PjpSummaryCard from "../../components/pjpApproval/PjpSummaryCard";
import NoDataMessage from "../../components/manageEvents/NoEventsMessage";
import { InfoOutlined } from "@material-ui/icons";
import { Tabs, Tab } from "@mui/material";
import dayjs from "dayjs";

function PjpApproval() {
  const { translate } = usePortalTranslation();
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showPjpLoader, setShowPjpLoader] = useState<boolean>(false);

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pjpData, setPjpData] = useState<any>(null);
  const [pjpDataNext, setPjpDataNext] = useState<any>(null);
  const [summaryData, setSummaryData] = useState<any>([]);
  const [summaryDataNext, setSummaryDataNext] = useState<any>([]);
  const [pjpDayCardData, setPjpDayCardData] = useState<any>([]);
  const [pjpDayCardDataNext, setPjpDayCardDataNext] = useState<any>([]);
  const [pjpPlanOption, setPjpPlanOption] = useState<any>(null);

  const [managerComment, setManagerComment] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  const currentMonth = dayjs().format("MMMM");
  const nextMonth = dayjs().add(1, "month").format("MMMM");
  const [selectedMonth, setSelectedMonth] = useState("curr");

  useEffect(() => {
    const fetchPjpPlanConfig = async () => {
      try {
        const config = await PjpPlanConfig();
        setPjpPlanOption(config);
      } catch (error) {
        console.error("Error fetching PjpPlanConfig:", error);
      }
    };

    fetchPjpPlanConfig();
  }, []);

  const fetchUsers = async () => {
    try {
      setShowLoader(true);
      const fetchedUsers = await getUser(pjpPlanOption);
      setUsers(fetchedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    if (!pjpPlanOption) return;

    fetchUsers();
  }, [pjpPlanOption]);

  useEffect(() => {
    if (!selectedUser || !pjpPlanOption) return;
    const fetchPjpData = async () => {
      try {
        const showPopUp = pjpPlanOption?.pjpApproval?.showPopUp ?? true;
        setShowPjpLoader(true);
        const PjpData = await getPjpData(selectedUser, pjpPlanOption);

        if (PjpData?.curr || PjpData?.next) {
          setPjpData(PjpData?.curr ?? {});
          setPjpDataNext(PjpData?.next ?? {});

          if (PjpData?.curr ) {
            const summaryData = transformPjpSummary(PjpData?.curr , pjpPlanOption, { showRoutesErrorPopup: showPopUp });
            setSummaryData(summaryData);

            const dayData = transformDayCard(PjpData?.curr , pjpPlanOption);
            setPjpDayCardData(dayData);
          }

          if (PjpData?.next) {
            const summaryData = transformPjpSummary(PjpData?.next, pjpPlanOption, { showRoutesErrorPopup: showPopUp });
            setSummaryDataNext(summaryData);

            const dayData = transformDayCard(PjpData?.next, pjpPlanOption);
            setPjpDayCardDataNext(dayData);
          }

        }else{
          setPjpData(PjpData);

          const summaryData = transformPjpSummary(PjpData, pjpPlanOption, { showRoutesErrorPopup: showPopUp });
          setSummaryData(summaryData);

          const dayData = transformDayCard(PjpData, pjpPlanOption);
          setPjpDayCardData(dayData);
        }
      
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setShowPjpLoader(false);
      }
    };

    fetchPjpData();
  }, [selectedUser, pjpPlanOption]);

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManagerComment(event.target.value);
    if (event.target.value.trim()) {
      setError(false);
    }
  };

 
  const resetStates = () => {
    setSelectedUser(null);
    setManagerComment("");
    setError(false);
    setPjpData(null);
    setSummaryData([]);
    setPjpDayCardData([]);
    setSelectedMonth("curr");
    // setPjpPlanOption(null);
  };

  const handleApprove = async (action: "approve" | "reject") => {
    const trimmedComment = managerComment.trim();
    if (!pjpPlanOption?.pjpApproval?.rejectCondition && !trimmedComment) {
      setError(true);

      openPopup(
        "Alert",
        "Please fill in Manager Comments before submitting",
        () => {}
      );
    } else if (pjpPlanOption?.pjpApproval?.rejectCondition && action === "reject" && trimmedComment.length < (parseInt(
      pjpPlanOption?.pjpApproval?.rejectCondition ?? "",
      10
    ) || 0)) {
      setError(true);

      openPopup(
        "Alert",
        `Please enter more than ${pjpPlanOption?.pjpApproval?.rejectCondition} characters in comment to reject the PJP`,
        () => {}
      );
    }else {
      setError(false);

      let data = ((pjpData && pjpData.length != 0) && (pjpDataNext && pjpDataNext.length != 0) && selectedMonth === "next") ? pjpDataNext : ((pjpData && pjpData.length != 0) ? pjpData : pjpDataNext);

      const res = await submitPjpApprovalData(
        data,
        managerComment,
        pjpPlanOption,
        action,
        selectedUser?.loginid
      );

      if (res === "") {
        const labelKey = action === "approve" ? "Approved" : "Rejected";
        openPopup("Success", `PJP has been successfully ${labelKey}.`, () => {
          resetStates();
          fetchUsers();
        });
      } else if (res === "failed") {
        openPopup(
          "Error",
          "Something went wrong. Please try again after some time!",
          () => {}
        );
      }
    }
  };

  const getOptionLabel = (option: User) => {
    if(pjpPlanOption?.pjpApproval){
      return `${option.name} - ${option.loginid}`;
    }
    return option.name;
  }

  
  return (
    <div className="pjp-approval-parent">
      <DefaultHeading
        heading={translate(TranslationEnum.pjp_approval, "PJP Approval")}
        showDefaultBackButton={true}
      />
      {showLoader ? (
        <div className="loader-center">
          <Loader />
        </div>
      )  : (
        <div className="pjp-approval-container">
          <Box
            sx={{
              display: "flex",
              margin: 2,
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 5,
            }}
          >
            <Autocomplete
              sx={{ width: "100%" }}
              options={users}
              getOptionLabel={(option) => getOptionLabel(option)}
              isOptionEqualToValue={(option, value) =>
                option.loginid === value.loginid
              }
              onChange={(event, newValue) => {setSelectedUser(newValue); setSelectedMonth("curr");}}
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate(TranslationEnum.pjp_approval, "Select User")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </Box>

          {showPjpLoader ? (
            <div className="loader-center">
              <Loader />
            </div>
          ) : users.length === 0 ? (
            <div className="loader-center">
              <NoDataMessage icon={<InfoOutlined />} message={translate(TranslationEnum.pjp_approval, "No data available.")} />
            </div>
          )
          :  (
            selectedUser &&
            ((!pjpPlanOption?.pjpApproval?.pjpApprovalCheck && (summaryData || summaryDataNext)) || summaryData) && (
              <div className="summaryCard">
                {(!pjpPlanOption?.pjpApproval?.pjpApprovalCheck && summaryData && summaryDataNext) && 
                  (<Box sx={{ display: "flex", mb: 2, ml: 2 }}>
                    <Tabs
                      value={selectedMonth}
                      onChange={(e, newValue) => setSelectedMonth(newValue)}
                      variant="standard"
                      TabIndicatorProps={{
                        style: { display: "none" }, 
                      }}
                      sx={{
                        backgroundColor: "#d5f2ee",
                        borderRadius: "12px",
                        p: 1,
                        display: "inline-flex",
                        border: "1.5px solid #6ab6ac"
                      }}
                    >
                      <Tab
                        label={currentMonth}
                        value="curr"
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          minWidth: 150,
                          fontWeight: 500,
                          marginRight: 1,
                          border: "1px solid grey",
                          "&.Mui-selected": {
                            backgroundColor: "#1C7EA0 !important",
                            color: "white !important",
                            border: "none",
                          },
                          "&:not(.Mui-selected)": {
                            backgroundColor: "white",
                            color: "#1C7EA0",
                          },
                        }}
                      />
                      <Tab
                        label={nextMonth}
                        value="next"
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          minWidth: 150,
                          fontWeight: 500,
                          border: "1px solid grey",
                          "&.Mui-selected": {
                            backgroundColor: "#1C7EA0 !important",
                            color: "white !important",
                            border: "none",
                          },
                          "&:not(.Mui-selected)": {
                            backgroundColor: "white",
                            color: "#1C7EA0",
                          },
                        }}
                      />
                    </Tabs>
                  </Box>)}
                <PjpSummaryCard
                  summaryData={
                    pjpPlanOption?.pjpApproval?.pjpApprovalCheck === false
                      ? (
                        summaryData && summaryDataNext
                          ? (selectedMonth === "next" ? summaryDataNext : summaryData)
                          : (summaryData ?? summaryDataNext)
                      )
                      : summaryData
                  }
                  dayCardData={
                    pjpPlanOption?.pjpApproval?.pjpApprovalCheck === false
                      ? (
                        (pjpDayCardData?.length && pjpDayCardDataNext?.length)
                          ? (selectedMonth === "next" ? pjpDayCardDataNext : pjpDayCardData)
                          : (pjpDayCardData?.length ? pjpDayCardData : pjpDayCardDataNext)
                      )
                      : pjpDayCardData
                  }
                  comment={managerComment}
                  onCommentChange={handleCommentChange}
                  error={error}
                  showMonth={!summaryData || !summaryDataNext}
                />
                <div className="button-container">
                  <Button variant="contained" onClick={() => handleApprove("approve")}>
                  {translate(TranslationEnum.pjp_approval, "APPROVE")}
                  </Button>
                  <Button variant="contained" onClick={() => handleApprove("reject")}>
                  {translate(TranslationEnum.pjp_approval, "REJECT")}
                  </Button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default PjpApproval;
